// Live notification poller — a single module-level poll loop, shared by every
// consumer (mirrors the `useActivityRail.js` singleton pattern), that hits the
// lightweight `GET /notifications/unread-count` endpoint. It feeds the live
// unread count into `CurrentUserContext` (so the TopBar bell badge re-renders)
// and fires an "arrival" callback exactly once per newly-seen notification so
// the shell can pop a toast with a deep link. No websockets — dead simple.
import { useEffect } from 'react';
import { api } from './apiClient.js';

// ~9s cadence with ±1.5s jitter so many tabs don't stampede the endpoint in
// lockstep. Kept intentionally coarse — this is a courtesy live-update, not a
// real-time channel.
const POLL_BASE_MS = 9000;
const POLL_JITTER_MS = 1500;

const countListeners = new Set(); // cb(count)
const arrivalListeners = new Set(); // cb({ count, previousCount, latest })

let pollerStarted = false;
let pollerTimeout = null;
let visibilityBound = false;
// `null` == not yet initialised. We never fire an arrival on the first poll of
// a session (that would toast every pre-existing unread on page load); we only
// toast when the count rises above a previously-known baseline.
let lastCount = null;
// Newest unread id we have already accounted for — de-dupes arrivals so a
// single new notification produces exactly one toast even across polls.
let lastSeenId = null;

function nextDelay() {
  const jitter = Math.round((Math.random() * 2 - 1) * POLL_JITTER_MS);
  return POLL_BASE_MS + jitter;
}

function documentHidden() {
  return typeof document !== 'undefined' && document.hidden === true;
}

async function pollOnce() {
  try {
    const res = await api.get('/notifications/unread-count');
    const count = Number(res?.data?.count ?? 0);
    const latest = res?.data?.latest || null;
    const previousCount = lastCount;
    const firstLoad = lastCount === null;

    for (const cb of countListeners) cb(count);

    // Arrival = the count rose above a known baseline AND the newest unread is
    // one we have not toasted yet. Never on first load; never on an unchanged
    // or falling count.
    if (!firstLoad && count > previousCount && latest && latest.id !== lastSeenId) {
      for (const cb of arrivalListeners) cb({ count, previousCount, latest });
    }

    lastCount = count;
    if (latest) lastSeenId = latest.id;
  } catch {
    // Ignore transient failures — the badge keeps its last known value and the
    // next tick retries.
  }
}

function schedule() {
  pollerTimeout = setTimeout(async () => {
    // Pause work while the tab is hidden to avoid pointless background churn,
    // but keep the loop alive so it resumes cleanly when the tab returns.
    if (!documentHidden()) {
      await pollOnce();
    }
    schedule();
  }, nextDelay());
}

function handleVisibility() {
  // Coming back to the tab should feel instant — refresh immediately rather
  // than waiting out the remaining interval.
  if (!documentHidden()) {
    pollOnce();
  }
}

function startPoller() {
  if (pollerStarted) return;
  pollerStarted = true;
  pollOnce(); // baseline load — establishes lastCount/lastSeenId, never toasts
  schedule();
  if (typeof document !== 'undefined' && !visibilityBound) {
    document.addEventListener('visibilitychange', handleVisibility);
    visibilityBound = true;
  }
}

function stopPoller() {
  if (pollerTimeout) clearTimeout(pollerTimeout);
  pollerTimeout = null;
  pollerStarted = false;
  if (typeof document !== 'undefined' && visibilityBound) {
    document.removeEventListener('visibilitychange', handleVisibility);
    visibilityBound = false;
  }
  // Reset the baseline so a fresh session (e.g. after logout/login) re-seeds
  // without toasting the incoming user's pre-existing unread notifications.
  lastCount = null;
  lastSeenId = null;
}

/**
 * Subscribe the calling component to the shared notification poll loop.
 *
 * @param {object}   opts
 * @param {boolean}  [opts.enabled]    Start/stop the shared poller.
 * @param {Function} [opts.onCount]    Called with the latest unread count on every poll.
 * @param {Function} [opts.onArrival]  Called once per newly-seen notification: ({count, previousCount, latest}).
 */
export function useNotificationPoll({ enabled = true, onCount, onArrival } = {}) {
  useEffect(() => {
    if (!onCount) return undefined;
    countListeners.add(onCount);
    return () => countListeners.delete(onCount);
  }, [onCount]);

  useEffect(() => {
    if (!onArrival) return undefined;
    arrivalListeners.add(onArrival);
    return () => arrivalListeners.delete(onArrival);
  }, [onArrival]);

  useEffect(() => {
    if (enabled) startPoller();
    else stopPoller();
  }, [enabled]);
}

// Exposed for tests to reset the module-level singleton between cases.
export function __resetNotificationPollForTests() {
  stopPoller();
  countListeners.clear();
  arrivalListeners.clear();
}
